/**
 * Insurance. Adults only — enforced by the Worker (worker/index.ts gates /api/insurance)
 * and mirrored by the route wrapper in App.tsx as a courtesy. Nothing here trusts the
 * front end.
 *
 * Coverage leads, shopping follows. The first version opened on the shopping board, so
 * the first thing on screen was an absence — "nothing bindable" — which is the right
 * headline only while you are actively shopping. The rest of the year the question is
 * "what do we hold and when does it renew", and that now answers first.
 */
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import type { InsuranceTypeConfig } from '../../shared/types';
import PacketPanel from './PacketPanel';
import PoliciesPanel from './PoliciesPanel';
import QuotesPanel from './QuotesPanel';
import { ErrorState, Loading, cx, useResource } from './ui';

const TABS = [
  { id: 'coverage', label: 'Coverage' },
  { id: 'shopping', label: 'Shopping' },
  { id: 'packet', label: 'Quote packet' },
] as const;

type TabId = (typeof TABS)[number]['id'];

export default function InsurancePage() {
  const [active, setActive] = useState<TabId>('coverage');
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const { data, loading, error, reload } = useResource<InsuranceTypeConfig[]>(
    () => api.get<InsuranceTypeConfig[]>('/insurance/types'),
    []
  );

  // Full arrow-key support, because unlike the line-of-coverage filter these really are
  // tabs: two panels, one visible at a time. The role is only honest with the behaviour.
  function onKeyDown(e: React.KeyboardEvent<HTMLButtonElement>, index: number) {
    let next = index;
    if (e.key === 'ArrowRight') next = (index + 1) % TABS.length;
    else if (e.key === 'ArrowLeft') next = (index - 1 + TABS.length) % TABS.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = TABS.length - 1;
    else return;

    e.preventDefault();
    setActive(TABS[next].id);
    tabRefs.current[next]?.focus();
  }

  return (
    <div>
      <div className="mb-6">
        <p className="text-sm text-ink-soft">
          <Link to="/money" className="underline underline-offset-2 hover:text-ink">
            Money
          </Link>{' '}
          / Insurance
        </p>
        <h1 className="mt-1 font-display text-3xl font-semibold text-ink">Insurance</h1>
        <p className="mt-1 max-w-prose text-ink-soft">
          What we carry, what it costs, when it renews &mdash; and, when we're shopping, how
          each quote measures against the coverage this house actually needs.
        </p>
      </div>

      <div
        role="tablist"
        aria-label="Insurance sections"
        className="mb-6 flex gap-2 overflow-x-auto border-b border-border pb-px"
      >
        {TABS.map((tab, index) => (
          <button
            key={tab.id}
            ref={(el) => {
              tabRefs.current[index] = el;
            }}
            role="tab"
            id={`tab-${tab.id}`}
            aria-selected={active === tab.id}
            aria-controls={`panel-${tab.id}`}
            tabIndex={active === tab.id ? 0 : -1}
            onClick={() => setActive(tab.id)}
            onKeyDown={(e) => onKeyDown(e, index)}
            className={cx(
              'min-h-11 shrink-0 rounded-t-lg border-b-2 px-4 text-sm font-medium whitespace-nowrap transition-colors',
              'focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2 focus-visible:ring-offset-canvas focus-visible:outline-none',
              active === tab.id
                ? 'border-sage text-sage'
                : 'border-transparent text-ink-soft hover:bg-surface-sunk hover:text-ink'
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div
        role="tabpanel"
        id={`panel-${active}`}
        aria-labelledby={`tab-${active}`}
        tabIndex={0}
        className="focus-visible:outline-none"
      >
        {active === 'coverage' && <PoliciesPanel />}
        {(active === 'shopping' || active === 'packet') && (
          <>
            {loading && <Loading label="Loading lines of coverage" rows={2} />}
            {error && !loading && <ErrorState message={error} onRetry={reload} />}
            {data && !loading && !error && active === 'shopping' && <QuotesPanel types={data} />}
            {data && !loading && !error && active === 'packet' && <PacketPanel types={data} />}
          </>
        )}
      </div>
    </div>
  );
}
